import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const logoLight = process.env.REACT_APP_LOGO_LIGHT || '/images/logo-white.png';
  const logoDark = process.env.REACT_APP_LOGO_DARK || '/images/logo.png';

  const isHomePage = location.pathname === '/';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);

    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;

    const bootstrapLink = document.getElementById('bootstrap-css');
    if (bootstrapLink) {
      if (newLang === 'ar') {
        bootstrapLink.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.rtl.min.css';
      } else {
        bootstrapLink.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css';
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const currentLang = i18n.language;
    document.documentElement.dir = currentLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLang;

    const bootstrapLink = document.getElementById('bootstrap-css');
    if (bootstrapLink) {
      if (currentLang === 'ar') {
        bootstrapLink.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.rtl.min.css';
      } else {
        bootstrapLink.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css';
      }
    }
  }, [i18n.language]);

  return (
    <nav className={`navbar navbar-expand-lg fixed-top ${scrolled || !isHomePage ? 'scrolled' : ''} ${i18n.language === 'ar' ? 'navbar-rtl' : 'navbar-ltr'}`}>
      <div className="container">
        <div className="mobile-top-row d-lg-none w-100 d-flex align-items-center justify-content-between">
          <button
            className="btn btn-sm btn-outline-secondary mobile-lang-btn"
            onClick={toggleLanguage}
            title={i18n.language === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
          >
            {i18n.language === 'ar' ? 'EN' : 'ع'}
          </button>

          <Link to="/" className="mobile-logo-link">
            <img
              src={scrolled || !isHomePage ? logoDark : logoLight}
              alt="Dr Hasan Haji Medical Center"
              className="mobile-logo"
              loading="lazy"
            />
          </Link>

          <div className="mobile-right-buttons d-flex align-items-center gap-2">
            {isAuthenticated && (user?.role === 'admin' || user?.role === 'reception' || user?.role === 'administration') && (
              <Link to="/admin" className="btn btn-sm btn-warning mobile-admin-btn" title={t('nav.dashboard')}>
                <i className="fas fa-cogs"></i>
              </Link>
            )}

            <Link to="/evaluation" className="btn btn-sm btn-primary mobile-eval-btn" title={t('nav.review')}>
              <i className="fas fa-poll" style={{ fontSize: '1.5em' }}></i>
            </Link>

          </div>
        </div>

        <div className="logo-container d-none d-lg-block">
          <Link to="/" className="custom-logo-link">
            <img
              src={scrolled || !isHomePage ? logoDark : logoLight}
              alt="Dr Hasan Haji Medical Center"
              className="custom-logo"
              loading="lazy"
            />
          </Link>
        </div>

        <div className="collapse navbar-collapse d-none d-lg-flex">
          <ul className="navbar-nav me-auto">
            <li className="nav-item">
              <Link className="nav-link" to="/services">
                {t('nav.services')}
              </Link>
            </li>

            <li className="nav-item">
              <Link className="nav-link" to="/insurance">
                {t('nav.insurance')}
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/packages">
                {t('nav.packages')}
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/medical-team">
                {t('nav.medicalTeam')}
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/evaluation">
                {t('nav.review')}
              </Link>
            </li>
          </ul>

          <div className="nav-buttons d-flex align-items-center">
            <div className="language-switcher me-3">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={toggleLanguage}
                title={i18n.language === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
              >
                {i18n.language === 'ar' ? 'EN' : 'ع'}
              </button>
            </div>

            {isAuthenticated ? (
              <div className="dropdown">
                <button
                  className="btn btn-primary dropdown-toggle"
                  type="button"
                  id="userMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {i18n.language === 'ar' ? (
                    <>
                      {user?.name}
                      <i className="fas fa-user-circle ms-2"></i>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-circle me-2"></i>
                      {user?.name}
                    </>
                  )}
                </button>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userMenuButton">
                  <li>
                    <Link className="dropdown-item" to="/profile">
                      {i18n.language === 'ar' ? (
                        <>
                          {t('nav.profile')}
                          <i className="fas fa-user-edit ms-2"></i>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-user-edit me-2"></i>
                          {t('nav.profile')}
                        </>
                      )}
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item" to="/analyses">
                      {i18n.language === 'ar' ? (
                        <>
                          {t('nav.myAnalyses')}
                          <i className="fas fa-calendar-alt ms-2"></i>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-calendar-alt me-2"></i>
                          {t('nav.myAnalyses')}
                        </>
                      )}
                    </Link>
                  </li>
                  {(user?.role === 'admin' || user?.role === 'reception' || user?.role === 'administration') && (
                    <li>
                      <Link className="dropdown-item" to="/admin">
                        {i18n.language === 'ar' ? (
                          <>
                            {t('nav.dashboard')}
                            <i className="fas fa-cogs ms-2"></i>
                          </>
                        ) : (
                          <>
                            <i className="fas fa-cogs me-2"></i>
                            {t('nav.dashboard')}
                          </>
                        )}
                      </Link>
                    </li>
                  )}

                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button className="dropdown-item" onClick={handleLogout}>
                      {i18n.language === 'ar' ? (
                        <>
                          {t('nav.logout')}
                          <i className="fas fa-sign-out-alt ms-2"></i>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sign-out-alt me-2"></i>
                          {t('nav.logout')}
                        </>
                      )}
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <div>
                <Link to="/login" className="btn btn-outline-primary me-2">
                  {t('nav.login')}
                </Link>
                <Link to="/register" className="btn btn-primary">
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .mobile-top-row {
          padding: 0.75rem 0;
        }

        .mobile-right-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .mobile-lang-btn,
        .mobile-eval-btn,
        .mobile-admin-btn {
          min-width: 45px;
          height: 45px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          font-weight: 600;
          font-size: 1rem;
        }

        .mobile-admin-btn {
          background: #ffc107;
          border-color: #ffc107;
          color: #000;
        }

        .mobile-admin-btn:hover {
          background: #e0a800;
          border-color: #d39e00;
        }

        .mobile-logo {
          height: 50px;
          width: auto;
          max-width: 150px;
          object-fit: contain;
        }

        @media (min-width: 992px) {
          .mobile-top-row {
            display: none !important;
          }
        }
      `}</style>
    </nav>
  );
};

export default Header;
